/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { cn } from '@/lib/utils';

interface CategoryDropdownContentSlotProps {
    page: PageWithComponentData;
    className?: string;
}

/**
 * Renders the `menu-{categoryId}` Page Designer content region inside a top-level nav
 * category's mega-menu dropdown — the column next to the category link list.
 *
 * Authors drop up to two components into the page's `content` region (typically a
 * MarkupContent block for the "icon + eyebrow + heading + copy + link" text, then a
 * pdImage for the lifestyle photo). Matching the reviewed mockup's column-impact (flex:1,
 * padded) + column-image (fixed 260px, edge-to-edge) split: with two components, the first
 * flexes and is padded, the second is pinned to 260px with no padding; with only one
 * component (order-independent — text-only or image-only), it expands to fill the full
 * width instead, so an unauthored second component never reserves an empty gap.
 */
export default function CategoryDropdownContentSlot({
    page,
    className,
}: CategoryDropdownContentSlotProps): ReactElement {
    return (
        <Region
            page={page}
            regionId="content"
            fallbackOnEmpty
            errorElement={null}
            className={
                className ??
                cn(
                    'flex items-stretch',
                    '[&>*:only-child]:min-w-0 [&>*:only-child]:flex-1 [&>*:only-child]:p-0',
                    '[&>*:first-child:not(:only-child)]:min-w-0 [&>*:first-child:not(:only-child)]:flex-1 [&>*:first-child:not(:only-child)]:p-0',
                    '[&>*:last-child:not(:only-child)]:w-[260px] [&>*:last-child:not(:only-child)]:h-full [&>*:last-child:not(:only-child)]:shrink-0 [&>*:last-child:not(:only-child)]:overflow-hidden'
                )
            }
        />
    );
}
